import {
  REQUEST_ITEM,
  SELECT_SHARE,
  SELECT_ITEM,
  INVALIDATE_SHARE,
  RECEIVE_SHARE,
  UNLOCK_SHARE,
  WRONG_PASSWORD,
  DECODE_TEXT,
  COPY_SHARE,
  SET_SHARE_COPY_STATUS,
  RECEIVE_COPY_SHARE_RESULT,
  RECIEVE_COPY_SHARE_JOB_DONE,
  NETWORK_ERROR,
  ShareCopyStatus,
  UPDATE_SHARE,
  UPDATE_NOTE,
  MOVE_NOTE,
  NOTE_MOVE_FAILED,
} from './share.actions';

const sanitizeAndEscapeURL = (input): string => {
  try {
    return input.replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#039;');
  } catch (e) {
    return '';
  }
}

export interface IShareState {
  share: IShareInstance;
  nodeId: string;
  shareCopyJobId: string;
  shareCopyNodeId: string;
  shareCopyStatus: ShareCopyStatus;
  result: {
    entities: {
      shares: {
        [id: number]: IShareInstance
      },
      notes: {
        [globalId: string]: INoteInstance
      },
      texts: {
        [globalId: string]: ITextInstance
      }
      attachments: {
        [globalId: string]: IAttachmentInstance
      }
    },
    result: number
  };
  isFetching: boolean;
  isInitialFetched: boolean;
  isInvalidated: boolean;
  isUnlocking: boolean;
  isPasswordRequired: boolean;
  isPasswordWrong: boolean;
  isNoteUploading: boolean;
  isNoteMoving: boolean;
  networkErrorCode: string;
}

export const shareInitialState: IShareState = {
  share: undefined,
  nodeId: undefined,
  shareCopyJobId: undefined,
  shareCopyNodeId: undefined,
  shareCopyStatus: ShareCopyStatus.SHARE_COPY_INITIAL,
  result: {
    entities: {
      notes: {},
      shares: {},
      texts: {},
      attachments: {}
    },
    result: undefined
  },
  isFetching: false,
  isInitialFetched: false,
  isPasswordRequired: false,
  isInvalidated: false,
  isUnlocking: false,
  isPasswordWrong: false,
  isNoteUploading: false,
  isNoteMoving: false,
  networkErrorCode: undefined,
};

export function shareReducer(state = shareInitialState, action: any) {
  switch (action.type) {
    case REQUEST_ITEM: {
      return {
        ...state,
        isFetching: true,
      }
    }
    case SELECT_SHARE:
      if (state.share
        && state.share.id === parseInt(action.id, 10)
        && state.share.securityKey === action.securityKey
        && !action.invalidate
      ) {
        return state;
      }
      return Object.assign({}, state, {
        share: {id: action.id, securityKey: action.securityKey},
        isInvalidated: true
      });
    case SELECT_ITEM:
      if ((!action.nodeId && (!state.nodeId || state.nodeId === state.share.noteGlobalId))
        || (state.nodeId === action.nodeId && !action.invalidate)
      ) {
        return state;
      }
      if (!state.share) {
        throw new Error('Not selected share');
      }
      const previousShare = state.share;
      const newShare = Object.assign({}, previousShare, {note: action.nodeId});
      return Object.assign({}, state, {
        share: newShare,
        nodeId: action.nodeId,
        isInvalidated: true
      });
    case INVALIDATE_SHARE:
      return Object.assign({}, state, {
        isInvalidated: true
      });
    case UNLOCK_SHARE:
      return Object.assign({}, state, {
        isInvalidated: true,
        isPasswordWrong: false
      });
    case RECEIVE_SHARE:
      const { result, fromClient } = action;
      const share = result.entities.shares[result.result];
      const isPasswordRequired = share.passwordRequired && !share.note;
      const isNoteUploading = !isPasswordRequired && !share.note;
      const stateChange = {
        isFetching: false,
        isInitialFetched: !!fromClient,
        isUnlocking: false,
        isInvalidated: false,
        isPasswordError: false,
        isNoteMoving: false,
        isPasswordRequired,
        isNoteUploading,
        result: action.result,
        share,
        nodeId: undefined
      };
      if (share.note) {
        stateChange.nodeId = share.note;
      }
      for (let k in stateChange.result.entities.notes) {
        if (stateChange.result.entities.notes[k]['url']) {
          stateChange.result.entities.notes[k]['url'] = sanitizeAndEscapeURL(stateChange.result.entities.notes[k]['url']);
        }
      }
      return Object.assign({}, state, stateChange);
    case UPDATE_SHARE:
      const updatedShare = {
        ...state.share,
        ...action.share,
      }
      return Object.assign({}, state, {
        share: updatedShare
      });
    case UPDATE_NOTE:
      const note = action.note;
      const noteId = action.noteId;
      const updatedNote = Object.assign({}, state.result.entities.notes[noteId], note);
      const updatedState = state;
      updatedState.result.entities.notes[noteId] = updatedNote;
      return Object.assign({}, state, updatedState);
    case DECODE_TEXT:
      const globalId = action.globalId;
      const text = action.text;
      state.result.entities.texts[action.globalId].text = text;
      return Object.assign({}, state);
    case RECEIVE_COPY_SHARE_RESULT:
      return Object.assign({}, state, {
        shareCopyJobId: action.result.id,
        shareCopyNodeId: action.result.targetNodeGlobalId,
      });
    case COPY_SHARE:
      return Object.assign({}, state, {
        shareCopyStatus: ShareCopyStatus.SHARE_COPY_IN_PROGRESS
      });
    case RECIEVE_COPY_SHARE_JOB_DONE:
      return Object.assign({}, state, {
        shareCopyJobId: undefined,
        shareCopyStatus: ShareCopyStatus.SHARE_COPY_DONE
      });
    case SET_SHARE_COPY_STATUS:
      return Object.assign({}, state, {
        shareCopyStatus: action.status
      });
    case NETWORK_ERROR:
      return Object.assign({}, state, {
        isFetching: false,
        isInitialFetched: true,
        networkErrorCode: action.code
      });
    case WRONG_PASSWORD:
      return Object.assign({}, state, {
        isPasswordWrong: true
      });
    case MOVE_NOTE:
      return Object.assign({}, state, {
        isNoteMoving: true
      });
    case NOTE_MOVE_FAILED:
      return Object.assign({}, state, {
        isNoteMoving: false,
      });
    default:
      return state;
  }
}

export interface IShareInstance {
  id: number;
  dateUpdated?: number;
  accessAllowed?: boolean;
  noteGlobalId?: string;
  note?: number;
  passwordRequired?: boolean;
  securityKey: string;
  userId?: number;
  userDisplayName?: string;
  parentNode?: INoteInstance;
  workspaceId?: string;
}

export interface INoteInstance {
  globalId: string;
  title: string;
  parentId: string;
}

export interface ITextInstance {
  globalId: string;
  text: string;
  textShort: string;
}

export interface IAttachmentInstance {
  globalId: string;
  dateAdded: number;
  dateUpdated: number;
  displayName: string;
  inList: boolean;
  isEncrypted: boolean;
  isScreenshot: boolean;
  mime: string;
  noteGlobalId: string;
  size: number;
  storedFileUUID: string;
  type: string;
}
